import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback } from "react";
import {
  type FieldArrayWithId,
  type UseFormReturn,
  useFieldArray,
  useForm,
} from "react-hook-form";
import * as Yup from "yup";

export interface Move {
  key: string;
  // Needed for the move request
  sourceLotId: string;
  quantityToMove: number;
  name: string;
  locationName: string;
  expiryDate: string;
  currentLotQuantity: number;
  locationId: string; // Stored in the form state so we can validate we are not attempting to move a lot to the location it's currently in
  externalReferences?: ReadonlyArray<{ origin: string }>;
}

export interface MoveFormValues {
  destinationLocationId: string;
  moves: Move[];
}

export interface MoveForm {
  form: UseFormReturn<MoveFormValues>;
  sourceLotFields: FieldArrayWithId<MoveFormValues, "moves", "sourceLotId">[];
  isLotSelected: (lotId: string) => boolean;
  handleLotClick: (moveDetails: Omit<Move, "quantityToMove">) => void;
  removeLot: (index: number) => void;
  clearLots: () => void;
}

const moveSchema = (destinationLocationId: string) =>
  Yup.object({
    sourceLotId: Yup.string().required(),
    locationId: Yup.string()
      .notOneOf(
        [destinationLocationId],
        "Can't move a lot to the same location",
      )
      .required(),
    quantityToMove: Yup.number()
      .integer()
      .min(1)
      .max(
        Yup.ref("currentLotQuantity"),
        "Must be less than or equal to the current quantity",
      ),
  });

const movesValidationSchema = Yup.object({
  destinationLocationId: Yup.string().required("Please select a location"),
  // Have to jump through these hoops to be able to reference the 'destinationLocationId' in the
  // child move schema because Yup.ref doesn't work across different Yup.objects
  moves: Yup.mixed()
    .when("destinationLocationId", (destinationLocationId) => {
      return Yup.array(moveSchema(destinationLocationId)).min(1);
    })
    .default([]),
});

export function useMoveForm(): MoveForm {
  const form = useForm<MoveFormValues>({
    mode: "onTouched",
    resolver: yupResolver(movesValidationSchema),
  });

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: "moves",
    keyName: "key",
  });

  const isLotSelected = useCallback(
    (lotId: string) =>
      !!fields.find(({ sourceLotId }) => sourceLotId === lotId),
    [fields],
  );

  const handleLotClick = useCallback(
    (moveDetails: Omit<Move, "quantityToMove">) => {
      const index = fields.findIndex(
        ({ sourceLotId }) => sourceLotId === moveDetails.sourceLotId,
      );
      if (index >= 0) {
        remove(index);
      } else {
        append(moveDetails as Move);
      }
    },
    [fields, append, remove],
  );

  const clearLots = useCallback(() => replace([]), [replace]);

  return {
    form,
    sourceLotFields: fields,
    isLotSelected,
    handleLotClick,
    removeLot: remove,
    clearLots,
  };
}
